import { useRouter } from 'next/router';
import qs from 'query-string';
import { useCallback, useMemo } from 'react';

import { CHECKOUT_STEPS, CheckoutStep } from '../types';
import {
  checkoutStepIndex,
  DEFAULT_CHECKOUT_STEP,
  isCheckoutStep,
} from '../utils/steps';

const STEP_PARAM = 'step';

export type CheckoutStepController = {
  goBack: () => void;
  goNext: () => void;
  goToStep: (step: CheckoutStep) => void;
  step: CheckoutStep;
};

const parseAsPath = (asPath: string) => {
  const [pathWithQuery] = asPath.split('#');
  const [pathname] = pathWithQuery.split('?');
  return { pathname, params: qs.parse(qs.extract(pathWithQuery)) };
};

const stepFromAsPath = (asPath: string): CheckoutStep => {
  const value = parseAsPath(asPath).params[STEP_PARAM];
  return isCheckoutStep(value) ? value : DEFAULT_CHECKOUT_STEP;
};

export const useCheckoutStep = (): CheckoutStepController => {
  const router = useRouter();
  const step = stepFromAsPath(router.asPath);

  const goToStep = useCallback(
    (nextStep: CheckoutStep) => {
      const { params, pathname } = parseAsPath(router.asPath);
      if (params[STEP_PARAM] === nextStep) return;

      void router.push(
        { pathname, query: { ...params, [STEP_PARAM]: nextStep } },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  const goNext = useCallback(() => {
    const nextIndex = checkoutStepIndex(stepFromAsPath(router.asPath)) + 1;
    if (nextIndex >= CHECKOUT_STEPS.length) return;

    goToStep(CHECKOUT_STEPS[nextIndex]);
  }, [goToStep, router]);

  const goBack = useCallback(() => {
    const previousIndex = checkoutStepIndex(stepFromAsPath(router.asPath)) - 1;
    if (previousIndex < 0) return;

    goToStep(CHECKOUT_STEPS[previousIndex]);
  }, [goToStep, router]);

  return useMemo(
    () => ({ goBack, goNext, goToStep, step }),
    [goBack, goNext, goToStep, step],
  );
};
